import { EVENTS } from "@/const/event.constants"
import { CHECKBOX_ISSUE_DATA_ID, ISSUES_LIMIT } from "@/const/table-constants"
import { preventDefaultWrapper } from "@/helpers/utils"
import { DetectionDetailsCell } from "@/molecules/DetectionDetailsCell"
import { HeaderCell } from "@/molecules/HeaderCell"
import { IssuesSummaryCell } from "@/molecules/IssuesSummaryCell"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale
} from "./date.helpers"

import type { DetectionDetails } from "@/api/useInventory.types"
import type { SortOrder } from "@/atoms"
import type { NumberConvertor } from "@/hooks/useNumberConvertor"
import type { ORDER_BY_PROPS } from "@/hooks/useOrderBy"
import type { TFunction } from "@/hooks/useTranslation"
import type Logger from "@/services/logger-adapter"

export const preventDisplayTitleOnCell = () => ({ title: "" })

export const getDefaultColumnParams = (
  dataIndex: string,
  sorter?: Omit<ORDER_BY_PROPS, "onSorterChange">,
  attributes: { customSorterName?: string; preventDefault?: boolean } = {}
) => {
  return {
    dataIndex,
    key: dataIndex,
    ellipsis: true,
    ...(attributes.preventDefault
      ? { onCell: () => ({ onClick: preventDefaultWrapper() }) }
      : undefined),
    ...(sorter
      ? {
          sorter: true,
          showSorterTooltip: false,
          defaultSortOrder:
            sorter.orderBy === dataIndex ? sorter.ascending : undefined,
          sortDirections: ["descend", "ascend", "descend"] as SortOrder[],
          ...attributes
        }
      : undefined)
  }
}

// A column the API orders by a name of its own, rather than by the column's
// data index; without a sorter it is not sortable
export const getOrderedColumnParams = (
  dataIndex: string,
  sorter: Omit<ORDER_BY_PROPS, "onSorterChange"> | undefined,
  orderBy: string = dataIndex
) => ({
  ...getDefaultColumnParams(
    dataIndex,
    sorter,
    sorter && orderBy !== dataIndex ? { customSorterName: orderBy } : {}
  ),
  ...(sorter
    ? {
        defaultSortOrder:
          sorter.orderBy === orderBy ? sorter.ascending : undefined
      }
    : undefined)
})

export const scrollToElement = (rowKey: string, index?: number) => {
  const element = document.querySelector(`[data-row-key="${rowKey}"]`)

  if (element) {
    const rect = element.getBoundingClientRect()
    const offsetProperty = getComputedStyle(element).getPropertyValue(
      "--offset-scroll-margin-top"
    )
    const offset = parseInt(offsetProperty, 10)

    if (rect.top < offset) {
      element.setAttribute("style", "background: var(--selected-bg-color)")

      if (index !== undefined)
        document.getElementById("PageWithTabsTemplate")?.scrollTo({
          top: 60 + index * 40,
          behavior: "smooth"
        })
      else
        element.scrollIntoView({
          behavior: "smooth",
          inline: "start"
        })

      setTimeout(() => {
        element.removeAttribute("style")
      }, 2000)
    }
  }
}

export const checkboxOnClick = <R extends { key: string }>(
  event: React.MouseEvent<HTMLElement>,
  props: {
    record: R
    cb: (record: R) => void
    logger?: Logger
    querySelector?: string
    analyticEvent?: Record<string, unknown>
  }
) => {
  const { record, cb, logger } = props

  try {
    const target = event.target as HTMLElement
    const { querySelector } = props

    if (
      target?.tagName?.toLowerCase() === "label" &&
      target.querySelector(querySelector || `[${CHECKBOX_ISSUE_DATA_ID}]`)
    )
      return

    logger?.info(EVENTS.ANALYTIC_EVENTS.DRAWER.OPENED, {
      id: record.key,
      ...props.analyticEvent
    })

    cb(record)
  } catch (err: any) {
    logger?.error(err)
  }
}

export const CELLS = {
  getIssuesSummaryCell: (
    props: {
      convertors: NumberConvertor
      t: TFunction
      sorter?: Omit<ORDER_BY_PROPS, "onSorterChange">
    },
    attr: Record<string, unknown> = {}
  ) => ({
    title: "Issues Summary",
    width: 230,
    render: IssuesSummaryCell(props, ISSUES_LIMIT),
    ...getDefaultColumnParams("severity", props.sorter),
    defaultSortOrder: "descend" as SortOrder,
    sortDirections: ["descend", "ascend", "descend"] as SortOrder[],
    ...attr
  }),
  getLastDetectedCell: (
    props: {
      t: TFunction
      entityName?: string
      content?: string
      width?: number
    } & ReturnType<typeof getDefaultColumnParams>
  ) => {
    const { content, entityName, t, ...rest } = props

    return {
      title: HeaderCell({
        title: t("general.lastScan"),
        Content: entityName
          ? t("general.detectionDetails.title", { entityName })
          : t(content || ""),
        whiteSpace: "pre"
      }),
      className: "capitalize-text-ks pre-white-space-ks",
      width: 150,
      render: DetectionDetailsCell(t),
      ...getDefaultColumnParams("lastDetected"),
      ...rest
    }
  }
}

export const getLastDetectedCellData = (record: DetectionDetails) => ({
  lastDetectionData: {
    discoveredAt: getDateInUserLocale(
      getDateFromSeconds(record.discoveredAt),
      DEFAULT_LONG_FORMAT
    ),
    lastSeen: getDateInUserLocale(
      getDateFromSeconds(record.lastSeen),
      DEFAULT_LONG_FORMAT
    ),
    lastScan: getDateInUserLocale(
      getDateFromSeconds(record.lastScan),
      DEFAULT_LONG_FORMAT
    )
  }
})
